import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  BadgeCheck,
  BriefcaseBusiness,
  Building2,
  CarFront,
  CreditCard,
  KeyRound,
  Landmark,
  MessageSquareOff,
  Package,
  Plane,
  Receipt,
  Search,
  ShieldAlert,
  Truck,
  Umbrella,
  Unplug,
  UserRoundX,
  WalletCards,
  Wifi,
  Wrench,
  Zap,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ALL_COMPANIES } from "@/data/companies";
import { normalizeSearchText, SOLUTION_FILTERS, SOLUTIONS } from "@/data/solutions";

const ICONS = {
  "badge-check": BadgeCheck,
  "briefcase-business": BriefcaseBusiness,
  building: Building2,
  "car-front": CarFront,
  "credit-card": CreditCard,
  "key-round": KeyRound,
  landmark: Landmark,
  "message-off": MessageSquareOff,
  package: Package,
  plane: Plane,
  receipt: Receipt,
  "shield-alert": ShieldAlert,
  truck: Truck,
  umbrella: Umbrella,
  unplug: Unplug,
  "user-x": UserRoundX,
  wallet: WalletCards,
  wifi: Wifi,
  wrench: Wrench,
  zap: Zap,
};

const THEME_CLASSES = {
  mint: "fs-solution-mint",
  ice: "fs-solution-ice",
  sand: "fs-solution-sand",
  coral: "fs-solution-coral",
  lilac: "fs-solution-lilac",
};

const DRAG_THRESHOLD = 8;

function getCompanyTerms(solution) {
  if (!solution.companyGroups?.length) return "";
  return ALL_COMPANIES
    .filter((company) => solution.companyGroups.includes(company.group))
    .map((company) => company.name)
    .join(" ");
}

export default function SolutionDiscovery() {
  const navigate = useNavigate();
  const trackRef = useRef(null);
  const dragRef = useRef(null);
  const [query, setQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState(null);
  const [soonSolution, setSoonSolution] = useState(null);
  const [scrollState, setScrollState] = useState({ start: true, end: false });

  const visibleSolutions = useMemo(() => {
    const normalizedQuery = normalizeSearchText(query.trim());
    return SOLUTIONS.filter((solution) => {
      if (activeFilter && !solution.filters.includes(activeFilter)) return false;
      if (!normalizedQuery) return true;
      const haystack = normalizeSearchText([
        solution.title,
        solution.description,
        solution.keywords.join(" "),
        getCompanyTerms(solution),
      ].join(" "));
      return normalizedQuery.split(/\s+/).every((term) => haystack.includes(term));
    });
  }, [activeFilter, query]);

  const updateScrollState = () => {
    const track = trackRef.current;
    if (!track) return;
    const max = track.scrollWidth - track.clientWidth;
    setScrollState({ start: track.scrollLeft <= 4, end: track.scrollLeft >= max - 4 });
  };

  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      if (trackRef.current) trackRef.current.scrollLeft = 0;
      updateScrollState();
    });
    return () => cancelAnimationFrame(frame);
  }, [visibleSolutions]);

  const scrollByPage = (direction) => {
    const track = trackRef.current;
    if (!track) return;
    track.scrollBy({ left: direction * Math.max(320, track.clientWidth * 0.78), behavior: "smooth" });
  };

  const openSolution = (solution) => {
    if (solution.status === "soon") {
      setSoonSolution(solution);
      return;
    }
    const params = new URLSearchParams({ source: "discovery", solution: solution.slug });
    if (solution.issue) params.set("issue", solution.issue);
    if (solution.flow) params.set("flow", solution.flow);
    if (solution.entryScreen) params.set("screen", solution.entryScreen);
    if (solution.preset) params.set("preset", solution.preset);
    navigate(`/dosya-baslat?${params.toString()}`);
  };

  const finishPointerInteraction = (event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    if (drag.moved) event.currentTarget.dataset.dragged = "true";
    if (
      drag.captured
      && event.currentTarget.hasPointerCapture?.(event.pointerId)
    ) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    dragRef.current = null;
    setTimeout(() => {
      if (trackRef.current) delete trackRef.current.dataset.dragged;
    }, 0);
  };

  return (
    <main className="fs-experience">
      <section className="fs-discovery-hero" data-testid="solution-discovery">
        <div className="fs-wide-container fs-discovery-hero-inner">
          <div className="fs-discovery-copy">
            <div className="fs-discovery-brand">
              ABONELİK İPTAL — GET IT RESOLVED.
            </div>
            <h1 className="fs-discovery-title">
              Bugün neyi çözmek istiyorsunuz?
            </h1>
            <p className="fs-discovery-description">
              Abonelik iptalinden para iadesine, şikâyetten resmî başvuruya kadar doğru yolu birlikte bulalım.
            </p>
          </div>

          <div className="fs-search-wrap">
            <Search className="fs-search-icon" size={21} aria-hidden="true" />
            <label htmlFor="solution-search" className="sr-only">Çözümlerde ara</label>
            <input
              id="solution-search"
              data-testid="solution-search"
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={'Sorununuzu yazın: “internet iptali”, “para iadesi”, “kargom gelmedi”...'}
              className="fs-discovery-search"
            />
          </div>

          <div className="fs-filter-scroll fs-hide-scrollbar" aria-label="Hızlı filtreler">
            <div className="fs-filter-row">
              {SOLUTION_FILTERS.map((filter) => {
                const selected = activeFilter === filter;
                return (
                  <button
                    key={filter}
                    type="button"
                    data-testid={`solution-filter-${filter}`}
                    aria-pressed={selected}
                    onClick={() => setActiveFilter(selected ? null : filter)}
                    className={`fs-filter-pill ${selected ? "fs-filter-pill-active" : ""}`}
                  >
                    {filter}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="fs-wide-container fs-solutions-section" aria-labelledby="solutions-title">
        <div>
          <div>
            <h2 id="solutions-title" className="fs-solutions-heading">
              Çözüm yollarını keşfedin
            </h2>
          </div>
        </div>

        {visibleSolutions.length > 0 ? (
          <div className="fs-carousel-shell">
            <div
              ref={trackRef}
              data-testid="solutions-carousel"
              className="fs-solutions-track"
              tabIndex={0}
              role="region"
              aria-label="İki satırlı çözüm kartları"
              onScroll={updateScrollState}
              onKeyDown={(event) => {
                if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
                  event.preventDefault();
                  scrollByPage(event.key === "ArrowRight" ? 1 : -1);
                }
              }}
              onPointerDown={(event) => {
                if (event.button !== undefined && event.button !== 0) return;
                dragRef.current = {
                  pointerId: event.pointerId,
                  pointerType: event.pointerType || "mouse",
                  x: event.clientX,
                  y: event.clientY,
                  left: event.currentTarget.scrollLeft,
                  moved: false,
                  captured: false,
                };
              }}
              onPointerMove={(event) => {
                const drag = dragRef.current;
                if (!drag || drag.pointerId !== event.pointerId) return;
                const deltaX = event.clientX - drag.x;
                const deltaY = event.clientY - drag.y;
                if (!drag.moved) {
                  if (Math.abs(deltaX) <= DRAG_THRESHOLD || Math.abs(deltaX) <= Math.abs(deltaY)) return;
                  drag.moved = true;
                  if (drag.pointerType !== "touch" && event.currentTarget.setPointerCapture) {
                    event.currentTarget.setPointerCapture(event.pointerId);
                    drag.captured = true;
                  }
                }
                if (drag.pointerType !== "touch") {
                  event.preventDefault();
                  event.currentTarget.scrollLeft = drag.left - deltaX;
                }
              }}
              onPointerUp={finishPointerInteraction}
              onPointerCancel={finishPointerInteraction}
            >
              {visibleSolutions.map((solution) => {
                const Icon = ICONS[solution.icon] || Wrench;
                return (
                  <button
                    key={solution.id}
                    type="button"
                    data-testid={`solution-card-${solution.id}`}
                    onClick={(event) => {
                      if (event.currentTarget.closest("[data-dragged='true']")) return;
                      openSolution(solution);
                    }}
                    className={`fs-solution-card ${solution.status === "soon" ? "fs-solution-card-soon" : ""} ${THEME_CLASSES[solution.theme]}`}
                  >
                    <span className="fs-solution-copy">
                      {solution.status === "soon" && <span className="fs-soon-badge">Yakında</span>}
                      <span className="fs-solution-title">{solution.title}</span>
                      <span className="fs-solution-description">{solution.description}</span>
                    </span>
                    <span className="fs-solution-icon" aria-hidden="true"><Icon size={25} strokeWidth={1.9} /></span>
                  </button>
                );
              })}
            </div>
            <div className="fs-carousel-controls" aria-label="Çözüm kartlarında gezinme">
              <button
                type="button"
                onClick={() => scrollByPage(-1)}
                disabled={scrollState.start}
                aria-label="Önceki çözüm kartları"
                data-testid="solutions-prev"
                className="fs-carousel-arrow fs-carousel-arrow-prev"
              >
                <ArrowLeft size={19} />
              </button>
              <button
                type="button"
                onClick={() => scrollByPage(1)}
                disabled={scrollState.end}
                aria-label="Sonraki çözüm kartları"
                data-testid="solutions-next"
                className="fs-carousel-arrow fs-carousel-arrow-next"
              >
                <ArrowRight size={19} />
              </button>
            </div>
          </div>
        ) : (
          <div data-testid="solution-empty-state" className="mt-7 rounded-3xl border border-[var(--feshet-ice)] bg-white p-7 md:p-9">
            <h3 className="text-xl font-bold text-[var(--feshet-text)]">Bu ifadeye uygun hazır bir çözüm bulamadık.</h3>
            <p className="mt-2 text-[var(--feshet-muted)] leading-relaxed">
              Sorununuzu başka kelimelerle arayabilir veya mevcut çözümlerden birini seçebilirsiniz.
            </p>
            <button
              type="button"
              className="fs-btn-outline mt-5"
              onClick={() => { setQuery(""); setActiveFilter(null); }}
            >
              Tüm çözümleri göster
            </button>
          </div>
        )}
      </section>

      <Dialog open={!!soonSolution} onOpenChange={(open) => { if (!open) setSoonSolution(null); }}>
        <DialogContent className="w-[calc(100%_-_2rem)] max-w-lg rounded-[2rem] border-0 bg-[var(--feshet-surface)] p-7 md:p-9">
          <DialogHeader>
            <div className="mb-3 w-12 h-12 rounded-2xl bg-[var(--feshet-ice)] text-[var(--feshet-petrol)] flex items-center justify-center">
              <Wrench size={22} aria-hidden="true" />
            </div>
            <DialogTitle className="text-2xl text-[var(--feshet-text)]">Bu çözüm hazırlanıyor</DialogTitle>
            <DialogDescription className="pt-2 text-base text-[var(--feshet-muted)] leading-relaxed">
              {soonSolution?.soonText}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-3">
            <button type="button" data-testid="soon-back-btn" onClick={() => setSoonSolution(null)} className="fs-btn-primary w-full sm:w-auto">
              Diğer çözümlere dön
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </main>
  );
}
